import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Spinner, ZoneChip, LageBadge } from "./ui";
import { X, Clock, TrendingDown, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

export default function SearchHistoryModal({ searchId, onClose }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/searches/${searchId}/history`).then(({ data }) => setData(data));
  }, [searchId]);

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
      />
      <div className="fixed inset-0 z-50 grid place-items-center p-4 pointer-events-none">
        <div
          className="border border-border bg-bg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto reveal"
          data-testid="search-history-modal"
        >
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div>
              <div className="overline">{t("searchHistory.timeline")}</div>
              <h3 className="font-heading text-2xl font-bold mt-1">
                {data?.search?.name || t("searchHistory.loading")}
              </h3>
              {data && (
                <div className="mt-1 text-[10px] font-mono text-text-muted">
                  {t("searchHistory.runsFired", { runs: data.total_runs, alerts: data.total_alerts })}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              data-testid="search-history-close"
              className="text-text-muted hover:text-white p-2"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {!data ? <Spinner /> : data.runs.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={28} className="mx-auto text-text-muted" />
                <p className="mt-3 text-xs font-mono text-text-secondary">
                  {t("searchHistory.noAlertsYet")}
                </p>
              </div>
            ) : (
              <ol className="space-y-5 relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-border" />
                {data.runs.map((run) => (
                  <li key={run.run_id} className="relative">
                    <div className="absolute left-[-22px] top-1 w-3 h-3 bg-signal-green border-2 border-bg" />
                    <div className="border border-border bg-surface p-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="overline">
                          {new Date(run.fired_at).toLocaleString("en-GB")}
                        </div>
                        <div className="text-[10px] font-mono text-text-muted">
                          {run.source ? `source=${run.source}` : ""} {run.green_count != null ? `· GREEN=${run.green_count}` : ""}
                        </div>
                      </div>
                      <div className="mt-1 num text-2xl text-signal-green">
                        {t("searchHistory.alertCount", { count: run.notifications.length, unit: run.notifications.length === 1 ? t("searchHistory.alertOne") : t("searchHistory.alertMany") })}
                      </div>
                      <div className="mt-3 space-y-2">
                        {run.notifications.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => { onClose(); nav(`/listings/${n.listing_id}`); }}
                            className="w-full flex items-center gap-3 p-2 border border-border bg-bg hover:bg-surfaceHover text-left transition-colors"
                          >
                            <ZoneChip zone={n.listing.zone} />
                            <LageBadge grade={n.listing.lage_grade} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white truncate">{n.listing.title || n.listing.market_name}</div>
                              <div className="text-[10px] font-mono text-text-muted flex items-center gap-3">
                                {n.listing.is_new && <span className="text-signal-green inline-flex items-center gap-1"><Sparkles size={10} /> NEW</span>}
                                {n.listing.price_drop_pct && <span className="text-signal-yellow inline-flex items-center gap-1"><TrendingDown size={10} /> −{n.listing.price_drop_pct}%</span>}
                                <span>{n.listing.market_name}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="num text-sm">{n.listing.faktor}</div>
                              <div className="num text-[10px] text-text-muted">€{(n.listing.price/1000).toFixed(0)}k</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
